// src/Components/BCM_Norms/FilterModal.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Select from 'react-select';

// ======================================================
// 🎨 Enhanced Modal Styles
// ======================================================
const styles = {
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    padding: '24px 24px 16px',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: '#f8fafc',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1e293b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  modalBody: {
    padding: '0 24px',
    flex: 1,
    overflowY: 'auto',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'white',
    paddingTop: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f1f5f9',
    marginBottom: '16px',
  },
  modalFooter: {
    padding: '20px 24px',
    borderTop: '1px solid #f1f5f9',
    backgroundColor: '#f8fafc',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center', // Align items vertically
    gap: '12px',
  },
  inputGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 500,
    color: '#374151',
    fontSize: '0.875rem',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '0.9rem',
    transition: 'all 0.2s ease-in-out',
    boxSizing: 'border-box',
    '&:disabled': {
        backgroundColor: '#f3f4f6',
        cursor: 'not-allowed',
    }
  },
  inputFocus: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    outline: 'none',
  },
  errorMessage: {
    color: '#ef4444',
    fontSize: '0.8rem',
    marginTop: '8px',
    margin: 0, // Reset margin for footer placement
  },
  ruleCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    position: 'relative',
  },
  ruleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  ruleTitle: {
    fontWeight: 600,
    color: '#475569',
    fontSize: '0.9rem',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '1.2rem',
    transition: 'background-color 0.2s ease',
  },
  removeButtonHover: {
    backgroundColor: '#fef2f2',
  },
  ruleGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px',
    alignItems: 'end',
  },
  addRuleButton: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 500,
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '24px',
  },
  addRuleButtonHover: {
    borderColor: '#3b82f6',
    color: '#3b82f6',
    backgroundColor: '#f0f9ff',
  },
  operatorSection: {
    padding: '20px',
    backgroundColor: '#f0f9ff',
    borderRadius: '12px',
    border: '2px solid #bfdbfe',
    position: 'relative',
  },
  operatorSelectContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  operatorSelect: {
    width: '180px',
  },
  operatorBadge: {
    padding: '6px 12px',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  button: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: 500,
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
  },
  buttonPrimary: {
    backgroundColor: '#3b82f6',
    color: 'white',
  },
  buttonPrimaryHover: {
    backgroundColor: '#2563eb',
    transform: 'translateY(-1px)',
  },
  buttonSecondary: {
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
  },
  buttonSecondaryHover: {
    backgroundColor: '#f9fafb',
    color: '#374151',
  },
  buttonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
    transform: 'none',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#6b7280',
  },
  loadingSpinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #f3f4f6',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  rulesCounter: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
};

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '4px 8px',
    fontSize: '0.9rem',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    minWidth: '100%',
    width: '400px',
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '300px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f0f9ff' : 'white',
    color: state.isSelected ? 'white' : '#374151',
    fontSize: '0.9rem',
    padding: '10px 12px',
  }),
};

const OPERATORS = {
  'categorical': [{ value: 'in', label: 'is one of' }, { value: 'not_in', label: 'is not one of' }],
  'numeric': [{ value: 'equals', label: '=' }, { value: 'not_equals', label: '≠' }, { value: 'greater_than', label: '>' }, { value: 'less_than', label: '<' }]
};

const LOGIC_CONFIG = {
  AND: { label: 'ALL (AND)' },
  OR: { label: 'ANY (OR)' }
};

const spinAnimation = `@keyframes spin { to { transform: rotate(360deg); } }`;
const styleSheet = document.createElement("style");
styleSheet.innerText = spinAnimation;
document.head.appendChild(styleSheet);

const FilterModal = ({ onClose, onSave, existingFilter, allFilters = [] }) => {
  const [name, setName] = useState(existingFilter?.name || '');
  const [rules, setRules] = useState(existingFilter?.rules?.rules || []);
  const [operator, setOperator] = useState(existingFilter?.rules?.operator || 'AND');
  const [variableOptions, setVariableOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredRemove, setHoveredRemove] = useState(null);
  const [hoveredAdd, setHoveredAdd] = useState(false);
  const [hoveredPrimary, setHoveredPrimary] = useState(false);
  const [hoveredSecondary, setHoveredSecondary] = useState(false);
  const [inputFocus, setInputFocus] = useState(false);
  const [isNameTouched, setIsNameTouched] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    const fetchVariableOptions = async () => {
      try {
        const response = await api.get('/bcm_norms/spss-files/all-filterable-variables/');
        setVariableOptions(response.data);
        if (!existingFilter && response.data.length > 0) {
          const firstField = response.data[0];
          setRules([{ field: firstField.id, op: OPERATORS[firstField.type]?.[0]?.value || 'in', value: firstField.type === 'categorical' ? [] : '' }]);
        }
      } catch (error) {
        console.error("Failed to fetch variable options", error);
        alert("Could not load filter options from the server.");
      } finally {
        setLoading(false);
      }
    };
    fetchVariableOptions();
  }, [existingFilter]);

  useEffect(() => {
    const trimmedName = name.trim().toLowerCase();
    if (trimmedName) {
      const isDuplicate = allFilters.some(filter => filter.name.toLowerCase() === trimmedName && filter.id !== existingFilter?.id);
      setNameError(isDuplicate ? 'A filter with this name already exists.' : '');
    } else {
      setNameError('');
    }
  }, [name, allFilters, existingFilter]);

  const handleRuleChange = (index, key, value) => {
    const newRules = [...rules];
    const oldField = newRules[index].field;
    newRules[index][key] = value;
    if (key === 'field' && value !== oldField) {
      const newFieldMeta = variableOptions.find(v => v.id === value);
      if (newFieldMeta) {
        newRules[index]['op'] = OPERATORS[newFieldMeta.type]?.[0]?.value || 'in';
        newRules[index]['value'] = newFieldMeta.type === 'categorical' ? [] : '';
      }
    }
    setRules(newRules);
  };

  const addRule = () => {
    if (variableOptions.length > 0) {
      const firstField = variableOptions[0];
      setRules([...rules, { field: firstField.id, op: OPERATORS[firstField.type]?.[0]?.value || 'in', value: firstField.type === 'categorical' ? [] : '' }]);
    }
  };

  const removeRule = (index) => setRules(rules.filter((_, i) => i !== index));

  const handleSave = async () => {
    const payload = { name: name.trim(), rules: { operator, rules } };
    try {
      const response = existingFilter?.id
        ? await api.put(`/bcm_norms/filters/${existingFilter.id}/`, payload)
        : await api.post('/bcm_norms/filters/', payload);
      onSave(response.data);
      onClose();
    } catch (error) {
      console.error("Failed to save filter", error);
      alert(`Error: Could not save filter. ${error.response?.data?.name?.[0] || error.response?.data?.detail || ''}`);
    }
  };

  const getFieldMeta = (fieldName) => variableOptions.find(f => f.id === fieldName);

  const getButtonStyle = (isPrimary, isHovered, isDisabled = false) => ({
    ...styles.button,
    ...(isPrimary ? styles.buttonPrimary : styles.buttonSecondary),
    ...(isHovered && !isDisabled && (isPrimary ? styles.buttonPrimaryHover : styles.buttonSecondaryHover)),
    ...(isDisabled && styles.buttonDisabled),
  });

  const getCurrentLogicConfig = () => LOGIC_CONFIG[operator];

  // --- MODIFICATION 1: Create the validation function for rules ---
  const isRuleValid = (rule) => {
    const meta = getFieldMeta(rule.field);
    if (!meta) return false; // This case should ideally not happen

    if (meta.type === 'numeric') {
      // Check if value is not null/undefined and is not an empty or whitespace-only string
      return rule.value != null && String(rule.value).trim() !== '';
    }
    if (meta.type === 'categorical') {
      // Check if value is an array and has at least one selected item
      return Array.isArray(rule.value) && rule.value.length > 0;
    }
    return true; // Default to valid for any other unforeseen types
  };
  
  // --- MODIFICATION 2: Check if all rules are valid ---
  const areAllRulesValid = rules.every(isRuleValid);

  if (loading) {
      return (
          <div style={styles.modalBackdrop}>
              <div style={styles.modalContent}><div style={styles.loadingContainer}><div style={styles.loadingSpinner}></div><p>Loading filter options...</p></div></div>
          </div>
      );
  }

  // --- MODIFICATION 3: Update isSaveDisabled with the new validation check ---
  const isSaveDisabled = !name.trim() || rules.length === 0 || !!nameError || !areAllRulesValid;
  const showBlankNameError = isNameTouched && !name.trim();

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}><span>🎯</span>{existingFilter ? 'Edit Filter' : 'Create New Filter'}</h2>
          <div style={styles.rulesCounter}>{rules.length} Rule{rules.length !== 1 ? 's' : ''}</div>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.stickyHeader}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Filter Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Young Males Q1 2024" style={{ ...styles.input, ...(inputFocus && styles.inputFocus) }} onFocus={() => setInputFocus(true)} onBlur={() => setIsNameTouched(true)} />
              {showBlankNameError && <p style={styles.errorMessage}>Filter name cannot be blank.</p>}
              {nameError && <p style={styles.errorMessage}>{nameError}</p>}
            </div>
            <div style={styles.operatorSection}>
              <div style={styles.operatorSelectContainer}>
                <label style={styles.label}>Combine rules using:</label>
                <div style={styles.operatorSelect}><Select options={[{ value: 'AND', label: LOGIC_CONFIG.AND.label }, { value: 'OR', label: LOGIC_CONFIG.OR.label }]} value={{ value: operator, label: getCurrentLogicConfig().label }} onChange={selected => setOperator(selected.value)} styles={selectStyles} /></div>
                <div style={styles.operatorBadge}>{operator} LOGIC</div>
              </div>
            </div>
          </div>
          <div style={{ paddingTop: '8px' }}>
            <label style={styles.label}>Filter Rules {rules.length > 0 && `(${rules.length} active)`}</label>
            {rules.map((rule, index) => {
              const fieldMeta = getFieldMeta(rule.field);
              const isFieldSelected = !!fieldMeta;
              const isCategorical = fieldMeta?.type === 'categorical';
              const operatorOptions = OPERATORS[fieldMeta?.type] || [];
              const fieldSelectOptions = variableOptions.map(opt => ({ value: opt.id, label: opt.name }));
              const currentFieldOption = fieldSelectOptions.find(opt => opt.value === rule.field);
              const valueSelectOptions = fieldMeta?.options?.map(opt => ({ value: opt, label: opt })) || [];
              const currentValueOptions = Array.isArray(rule.value) ? rule.value.map(v => ({ value: v, label: v })) : [];
              return (
                <div key={index} style={styles.ruleCard}>
                  <div style={styles.ruleHeader}>
                    <span style={styles.ruleTitle}>Rule {index + 1} {fieldMeta && `- ${fieldMeta.name}`}</span>
                    {rules.length > 1 && (<button style={{ ...styles.removeButton, ...(hoveredRemove === index && styles.removeButtonHover) }} onClick={() => removeRule(index)} onMouseEnter={() => setHoveredRemove(index)} onMouseLeave={() => setHoveredRemove(null)} title="Remove rule">×</button>)}
                  </div>
                  <div style={styles.ruleGrid}>
                    <div><label style={styles.label}>Field</label><Select options={fieldSelectOptions} value={currentFieldOption} onChange={selected => handleRuleChange(index, 'field', selected.value)} styles={selectStyles} /></div>
                    <div><label style={styles.label}>Operator</label><Select options={operatorOptions} value={operatorOptions.find(opt => opt.value === rule.op)} onChange={selected => handleRuleChange(index, 'op', selected.value)} styles={selectStyles} isDisabled={!isFieldSelected} placeholder={isFieldSelected ? "Select..." : "Select field first"} /></div>
                    <div><label style={styles.label}>Value</label>{isCategorical ? (<Select isMulti options={valueSelectOptions} value={currentValueOptions} onChange={selected => handleRuleChange(index, 'value', selected ? selected.map(s => s.value) : [])} styles={selectStyles} isDisabled={!isFieldSelected} placeholder={isFieldSelected ? "Select values..." : "Select field first"} />) : (<input type="number" value={rule.value} onChange={e => handleRuleChange(index, 'value', e.target.value)} style={styles.input} disabled={!isFieldSelected} placeholder={isFieldSelected ? "Enter number..." : "Select field first"} />)}</div>
                  </div>
                </div>
              );
            })}
            <button style={{ ...styles.addRuleButton, ...(hoveredAdd && styles.addRuleButtonHover) }} onClick={addRule} onMouseEnter={() => setHoveredAdd(true)} onMouseLeave={() => setHoveredAdd(false)}>+ Add Another Rule</button>
          </div>
        </div>
        <div style={styles.modalFooter}>
          {/* --- MODIFICATION 4: Add the validation error message to the footer --- */}
          {!areAllRulesValid && rules.length > 0 && (
            <p style={{ ...styles.errorMessage, marginRight: 'auto' }}>
              All rule values must be filled.
            </p>
          )}
          <button style={getButtonStyle(false, hoveredSecondary)} onClick={onClose} onMouseEnter={() => setHoveredSecondary(true)} onMouseLeave={() => setHoveredSecondary(false)}>Cancel</button>
          <button style={getButtonStyle(true, hoveredPrimary, isSaveDisabled)} onClick={handleSave} disabled={isSaveDisabled} onMouseEnter={() => setHoveredPrimary(true)} onMouseLeave={() => setHoveredPrimary(false)}>{existingFilter ? 'Update Filter' : 'Create Filter'}</button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;